import React from "react"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from "@material-ui/core"
import { X } from "react-feather"
import { useDebounce } from "../../../hooks/use-debounce"
import { ReportedHourViewModel } from "../../api/reported-hours"
import { useHourReportMutations } from "../../hooks/use-hour-report-mutations"
import { useHourReportFormOptions } from "../../hooks/use-form-options"
import { convertHoursAndMinutesToDecimalHours, getHHmmFromDate } from "../../utils"
import IntervalEntryFields from "../IntervalEntryFields"
import ManualEntryFields from "../ManualEntryFields"
import { ProjectSelector } from "../ProjectSelector"
import { TaskSelector } from "../TaskSelector/TaskSelector"
import type { ManualEntryValue } from "../ManualEntryFields"
import { useTranslation } from "react-i18next"

type HourReportCorrectionRole = "reporter" | "checker" | "approver" | "hour-report-admin"

type HourReportCorrectionFormProps = {
  setEditingReportId: (reportId: string | null) => void
  report: ReportedHourViewModel | undefined
  editableAs: HourReportCorrectionRole | null
}

type HourReportCorrection =
  | { description: string | null }
  | { projectId: string | null }
  | { taskId: string | null }
  | { hours: number }
  | { startTime: string; endTime: string }

export default function HourReportCorrectionForm({
  setEditingReportId,
  report,
  editableAs,
}: HourReportCorrectionFormProps) {
  const { t } = useTranslation("hour-report")
  const { projectOptions } = useHourReportFormOptions()
  const {
    updateHourReportInfo,
    updateHourReportProject,
    updateHourReportTask,
    updateHourReportHours,
    checkHourReport,
    approveHourReport,
    performHourReportAdminAction,
  } = useHourReportMutations()

  const updateAsReporter = React.useCallback(
    (reportId: string, changes: HourReportCorrection) => {
      if ("description" in changes) {
        return updateHourReportInfo(reportId, { description: changes.description ?? null })
      }

      if ("projectId" in changes) {
        return updateHourReportProject(reportId, { projectId: changes.projectId ?? null })
      }

      if ("taskId" in changes) {
        return updateHourReportTask(reportId, { taskId: changes.taskId ?? null })
      }

      if ("hours" in changes) {
        return updateHourReportHours(reportId, { hours: changes.hours })
      }

      return updateHourReportHours(reportId, { startTime: changes.startTime, endTime: changes.endTime })
    },
    [updateHourReportHours, updateHourReportInfo, updateHourReportProject, updateHourReportTask]
  )

  const updateHourReport = React.useCallback(
    (reportId: string, role: HourReportCorrectionRole, changes: HourReportCorrection) => {
      switch (role) {
        case "hour-report-admin":
          return performHourReportAdminAction(reportId, { action: "update", changes })
        case "approver":
          return approveHourReport(reportId, changes)
        case "checker":
          return checkHourReport(reportId, changes)
        case "reporter":
          return updateAsReporter(reportId, changes)
      }
    },
    [approveHourReport, checkHourReport, performHourReportAdminAction, updateAsReporter]
  )

  const updateDescription = React.useCallback(
    (reportId: string, role: HourReportCorrectionRole, description: string | null) => {
      return updateHourReport(reportId, role, { description })
    },
    [updateHourReport]
  )

  const { debouncedFn: debouncedUpdateDescription, flush: flushThrottledUpdate } = useDebounce(updateDescription, 500, {
    leading: true,
    trailing: true,
    flushOnUnmount: true,
  })

  const flushPendingUpdates = React.useCallback(() => {
    flushThrottledUpdate()
  }, [flushThrottledUpdate])

  const handleClose = () => {
    flushPendingUpdates()
    setEditingReportId(null)
  }

  const handleProjectChange = (projectId: string | null) => {
    if (!report || !editableAs) return
    return updateHourReport(report.id, editableAs, { projectId })
  }

  const handleTaskChange = (taskId: string | null) => {
    if (!report || !editableAs) return
    return updateHourReport(report.id, editableAs, { taskId })
  }

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!report || !editableAs) return
    const description = event.target.value.trim()
    debouncedUpdateDescription(report.id, editableAs, description || null)
  }

  const handleManualDurationChange = (value: ManualEntryValue) => {
    if (!report?.isReportedManually || !editableAs) return
    const hours = convertHoursAndMinutesToDecimalHours(value.hours, value.minutes)
    return updateHourReport(report.id, editableAs, { hours })
  }

  const handleStartChange = (start: Date | null) => {
    if (!report || report.isReportedManually || !start || !editableAs) return
    const timeUpdate = { startTime: getHHmmFromDate({ date: start }), endTime: report.endTime! }
    return updateHourReport(report.id, editableAs, timeUpdate)
  }

  const handleEndChange = (end: Date | null) => {
    if (!report || report.isReportedManually || !end || !editableAs) return
    const timeUpdate = {
      startTime: report.startTime!,
      endTime: getHHmmFromDate({ date: end, midnightAs24Hour: true }),
    }

    return updateHourReport(report.id, editableAs, timeUpdate)
  }

  React.useEffect(() => {
    window.addEventListener?.("beforeunload", flushPendingUpdates)

    return () => {
      window.removeEventListener?.("beforeunload", flushPendingUpdates)
    }
  }, [flushPendingUpdates])

  if (!report || !editableAs) return null

  return (
    <Dialog onClose={handleClose} open maxWidth="sm" fullWidth>
      <Box style={{ position: "absolute", right: 16, top: 8, zIndex: 1 }}>
        <IconButton edge="end" color="inherit" onClick={handleClose}>
          <X size={20} />
        </IconButton>
      </Box>
      <DialogTitle>{t("dialogs.correctTitle", { defaultValue: "Correct hour report" })}</DialogTitle>

      <DialogContent style={{ paddingBottom: 24 }}>
        <ProjectSelector
          selectedProjectId={report.projectId}
          onChange={handleProjectChange}
          projectOptions={projectOptions}
        />
        {report.projectId ? (
          <TaskSelector projectId={report.projectId} selectedTaskId={report.taskId} onChange={handleTaskChange} />
        ) : null}
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            {t("form.description", { defaultValue: "Description" })}
          </Typography>
          <TextField
            name="description"
            defaultValue={report.description || ""}
            onKeyDown={(e) => e.stopPropagation()}
            onChange={handleDescriptionChange}
            variant="outlined"
            margin="dense"
            minRows={2}
            multiline
            fullWidth
          />
        </Box>

        {report.isReportedManually ? (
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              {t("form.timeUsed", { defaultValue: "Time used" })}
            </Typography>
            <ManualEntryFields
              key={report.id}
              hours={report.durationInHoursAndMinutes.hours}
              minutes={report.durationInHoursAndMinutes.minutes}
              onChange={handleManualDurationChange}
              hoursLabel={t("form.hours", { defaultValue: "Hours" })}
              minutesLabel={t("form.minutes", { defaultValue: "Minutes" })}
            />
          </Box>
        ) : null}

        {report.isReportedByInterval || report.isReportedByTimer ? (
          <Box mt={3}>
            <IntervalEntryFields
              date={report.date}
              start={report.startTime!}
              end={report.endTime!}
              onStartChange={handleStartChange}
              onEndChange={handleEndChange}
              startLabel={t("form.startTime", { defaultValue: "Start Time" })}
              endLabel={t("form.endTime", { defaultValue: "End Time" })}
              durationDescription={(hours, minutes) =>
                t("form.duration", { defaultValue: "Time used: {{hours}}h {{minutes}}m", hours, minutes })
              }
              isClearable={false}
            />
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
